import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from "@mui/material";

import { RoomData, VenueFormData } from "../../../types/venue";
import VenuePreview from "./VenuePreview";
import patternUrl from "../../../assets/pattern.png";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";

interface AddVenueLayoutProps {
  steps: string[];
  activeStep: number;
  formData: VenueFormData;
  onStepClick?: (step: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: () => void;
  onRoomsChange?: (rooms: RoomData[]) => void;

  editingContactId: string | null;
  setEditingContactId: (id: string | null) => void;
  isSubmitting: boolean;
  children: React.ReactNode;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
  onStepChange: (step: number) => void;
}

// ── styled components ──────────────────────────────────────────────────────

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 15 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "rgba(255,255,255,0.6)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: "#fff" },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: 1,
  },
}));

const CustomStepIcon = styled("div")<{
  active?: boolean;
  completed?: boolean;
  browndark?: string;
}>(({ active, completed, browndark }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: completed
    ? "#fff"
    : active
      ? "#fff"
      : "rgba(255,255,255,0.2)",
  color: completed
    ? (browndark ?? "#724E41")
    : active
      ? (browndark ?? "#724E41")
      : "rgba(255,255,255,0.6)",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: active ? "0 0 0 3px rgba(255,255,255,0.4)" : "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
}));

// ── component ──────────────────────────────────────────────────────────────

export default function AddVenueLayout({
  steps,
  activeStep,
  formData,
  onNext,
  onPrevious,
  onSubmit,
  onStepClick,
  onRoomsChange,
  isSubmitting,
  children,
  setFormData,
  onStepChange,

  setEditingContactId,
}: AddVenueLayoutProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const color10 = reduxTheme.primary_colors?.[10];

  const color400 = reduxTheme.primary_colors?.[400];

  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: color800, p: 3 }}>
      <Grid container spacing={3}>
        {/* ── Main Form Area ── */}
        <Grid size={{ xs: 12, lg: 8 }} sx={{ display: "flex" }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: 4,
              maxHeight: "calc(100vh - 64px)",
              display: "flex",
              flexDirection: "column",
              backgroundColor: color900,
              boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
              overflow: "hidden",
            }}
          >
            {/* ── Pattern Header with Stepper ── */}
            <Box
              sx={{
                p: 3,
                pb: 2,
                pt: 5,
                position: "relative",
                overflow: "hidden",
                height: 120,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundImage: `linear-gradient(135deg, ${color400} 0%, ${color10} 50%, ${color400} 100%), url(${patternUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundBlendMode: "multiply",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  connector={<CustomConnector />}
                >
                  {steps.map((label, index) => (
                    <Step
                      key={label}
                      onClick={() => onStepClick?.(index)}
                      sx={{ cursor: "pointer" }}
                    >
                      <StepLabel
                        StepIconComponent={() => (
                          <CustomStepIcon
                            active={index === activeStep}
                            completed={index < activeStep}
                            browndark={color400}
                          >
                            {index + 1}
                          </CustomStepIcon>
                        )}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              index <= activeStep
                                ? "#fff"
                                : "rgba(255,255,255,0.5)",
                            fontWeight: index === activeStep ? 700 : 400,
                            letterSpacing: "0.02em",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>

            {/* ── Step Content ── */}
            {/* ── Step Content ── */}
            <Box
              sx={{
                p: 4,
                flex: 1,
                overflowY: "auto",
                backgroundColor: color900,

                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: color800,
                  borderRadius: "10px",
                },
              }}
            >
              {children}
            </Box>

            {/* ── Footer Navigation ── */}
            {/* ── Footer Navigation ── */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: `1px solid ${color800}`,
                backgroundColor: color900,
              }}
            >
              {/* Left Side: Text-only buttons */}
              <Box sx={{ display: "flex", gap: 3 }}>
                <Button
                  onClick={onPrevious}
                  disabled={activeStep === 0 || isSubmitting}
                  sx={{
                    color: "#000", // Adjust to your preferred dark text color
                    textTransform: "none",
                    textDecoration: "underline",
                    fontSize: "14px",
                    minWidth: "auto",
                    p: 0,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                    "&.Mui-disabled": { opacity: 0.3 },
                  }}
                >
                  Previous
                </Button>

                <Button
                  onClick={onSubmit}
                  disabled={isSubmitting}
                  sx={{
                    color: "#000",
                    textTransform: "none",
                    textDecoration: "underline",
                    fontSize: "14px",
                    minWidth: "auto",
                    p: 0,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Save Venue
                </Button>
              </Box>

              {/* Right Side: Primary Action Button */}
              <Button
                variant="contained"
                onClick={isLastStep ? onSubmit : onNext}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting && isLastStep ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
                sx={{
                  color: "#fff",
                  backgroundColor: color400, // This is your brown color
                  px: 6,
                  py: 1.5,
                  borderRadius: "8px", // Matches the slightly rounded corners in your image
                  textTransform: "none",
                  fontSize: "14px",
                  width: "90px",
                  height: "40px",
                  "&:hover": { backgroundColor: color10 },
                }}
              >
                {isSubmitting && isLastStep
                  ? "Saving..."
                  : isLastStep
                    ? "Save"
                    : "Next"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* ── Preview Panel ── */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box
            sx={{
              position: "sticky",
              top: 24,
              height: "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <VenuePreview
              formData={formData}
              currentStep={activeStep}
              onRoomsChange={onRoomsChange}
              setFormData={setFormData}
              setEditingContactId={setEditingContactId}
              onStepChange={onStepChange}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
